import { createContext, useContext, useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useHistory } from 'react-router-dom'
import axios from 'axios'
import { toast } from 'react-toastify'
import { getTaskStatus } from '../services/uploadService'

// Global live-analysis state — supports MULTIPLE concurrent runs.
//
// Mounted in Layout.jsx, above the private-route <Switch>, so it stays mounted
// across route changes. Two things follow from that:
//
//  1. Navigating away from the page that started a run no longer unmounts its
//     SSE stream or status polling — logs keep arriving in the background.
//  2. Runs are keyed by taskId, and each gets its OWN <RunPoller> child owning
//     its OWN EventSource + poll timer. Starting a run in folder B therefore
//     cannot tear down a run still going in folder A (which is exactly what a
//     single shared taskId-keyed effect did).
//
// NOTE: state lives in memory, so this covers SPA navigation only — a hard
// reload or a duplicated tab starts with an empty map and will not show runs
// started elsewhere. Persisting taskIds (or an endpoint listing a user's
// in-flight tasks) would be needed for that.
const AnalysisRunContext = createContext(null)

export function useAnalysisRun() {
  const ctx = useContext(AnalysisRunContext)
  if (!ctx) {
    throw new Error('useAnalysisRun must be used within <AnalysisRunProvider>')
  }
  return ctx
}

const TERMINAL = ['SUCCESS', 'FAILURE', 'CANCELLED']
export const isTerminalStatus = (s) => TERMINAL.includes(s)

// The modal's stepper has 4 phases (indices 0-3) and styles `idx === phase` as
// the CURRENT (orange, pulsing) step. A finished run must therefore sit PAST
// the last index so every chip renders green/done — `phase: 3` left the final
// "Complete" chip looking mid-flight.
const PHASE_COMPLETE = 4

const initialLogs = (meta) => ([
  { timestamp: 0, level: 'info', message: 'Initializing analysis...' },
  { timestamp: 0, level: 'success', message: `Starting ${meta.playName || 'analysis'} for ${meta.fileCount ?? 0} file(s)` },
  { timestamp: 0.1, level: 'info', message: 'Connecting to SourceOptima AI...' },
])

export function AnalysisRunProvider({ children }) {
  const history = useHistory()
  const BASE_URL = process.env.REACT_APP_BE_MAIN_URL

  // taskId -> run:
  //   { taskId, meta, status, progress, logs, completedResult, isStopping,
  //     startTime, pollInterval, concurrentUsers, isThrottled, pendingAssemblyId }
  const [runs, setRuns] = useState({})

  // Which run's full log modal is open. null = nothing open (indicator only).
  const [openTaskId, setOpenTaskId] = useState(null)

  // Per-run page callbacks, held in a ref so page re-renders never restart a poller.
  const handlersRef = useRef({})

  // Latest runs snapshot, for callbacks that must not depend on `runs` identity.
  const runsRef = useRef(runs)
  useEffect(() => { runsRef.current = runs }, [runs])

  // Same for the open run: handleTerminal must see which run the user is
  // watching AT COMPLETION TIME, not whichever value was captured when the
  // callback was last created.
  const openTaskIdRef = useRef(openTaskId)
  useEffect(() => { openTaskIdRef.current = openTaskId }, [openTaskId])

  // Patch one run without disturbing the others.
  const patchRun = useCallback((taskId, patch) => {
    setRuns(prev => {
      const run = prev[taskId]
      if (!run) return prev
      const next = typeof patch === 'function' ? patch(run) : patch
      return { ...prev, [taskId]: { ...run, ...next } }
    })
  }, [])

  const removeRun = useCallback((taskId) => {
    setRuns(prev => {
      if (!prev[taskId]) return prev
      const next = { ...prev }
      delete next[taskId]
      return next
    })
    delete handlersRef.current[taskId]
    setOpenTaskId(prev => (prev === taskId ? null : prev))
  }, [])

  const startRun = useCallback((taskId, meta = {}, handlers = {}) => {
    // Guard against the backend handing back a task_id that is already in
    // flight. Two runs sharing one id would collapse into a single entry in
    // this map, so the second folder would silently display the first run's
    // status. Surface it loudly instead of showing wrong data.
    if (runsRef.current[taskId]) {
      const existing = runsRef.current[taskId]
      console.error(
        '[AnalysisRun] DUPLICATE task_id from backend:', taskId,
        '\n  already in flight for folder:', existing.meta?.folderName,
        '(status', existing.status + ')',
        '\n  newly requested for folder:', meta.folderName,
      )
      toast.error(
        `Server returned an analysis id already running in "${existing.meta?.folderName}". ` +
        `The run for "${meta.folderName}" cannot be tracked separately.`
      )
      setOpenTaskId(taskId)
      return
    }
    console.log('[AnalysisRun] startRun', taskId, 'folder:', meta.folderName, 'play:', meta.playName)

    handlersRef.current[taskId] = {
      onComplete: handlers.onComplete || null,
      onError: handlers.onError || null,
      onStop: handlers.onStop || null,
      onClose: handlers.onClose || null,
    }
    setRuns(prev => ({
      ...prev,
      [taskId]: {
        taskId,
        meta,
        status: 'PENDING',
        progress: null,
        logs: initialLogs(meta),
        completedResult: null,
        isStopping: false,
        startTime: Date.now(),
        pollInterval: 500,
        concurrentUsers: 0,
        isThrottled: false,
        pendingAssemblyId: null,
      },
    }))
    setOpenTaskId(taskId) // a freshly started run opens its modal
  }, [])

  const showRun = useCallback((taskId) => setOpenTaskId(taskId), [])
  const minimizeRun = useCallback(() => setOpenTaskId(null), [])

  const appendLog = useCallback((taskId, entry) => {
    patchRun(taskId, run => ({ logs: [...run.logs, entry] }))
  }, [patchRun])

  // ---- Completion ------------------------------------------------------
  // A finished run auto-opens its modal. Runs that finished earlier stay in
  // the list marked complete, so their "View Results" button is still
  // reachable rather than being replaced by the newer completion.
  const handleTerminal = useCallback((taskId, { status, result, error }) => {
    const handlers = handlersRef.current[taskId] || {}
    const run = runsRef.current[taskId]

    if (status === 'SUCCESS') {
      const assemblyId = result?.assembly_id
      // Auto-navigation is only for the run the user is actually watching; a
      // background assembly run must not yank them out of another folder.
      const wasWatching = openTaskIdRef.current === taskId

      // Build the final progress from the RESULT's own counts, not by
      // spreading the last mid-run snapshot — that left stale fields behind
      // (e.g. "Processing 0/2 files" and current:0 sitting next to 100%).
      const total = result?.total ?? run?.progress?.total ?? run?.meta?.fileCount ?? 0
      const successful = result?.successful ?? 0
      const failed = result?.failed ?? 0
      const cachedCount = result?.cached ?? 0
      patchRun(taskId, r => ({
        status: 'SUCCESS',
        logs: result?.status_log || r.logs,
        progress: {
          stage: 'complete',
          phase: PHASE_COMPLETE,
          current: total,
          total,
          progress_pct: 100,
          successful,
          failed,
          cached: cachedCount,
          message: failed
            ? `Completed ${successful} of ${total} files — ${failed} failed`
            : `Completed ${successful} of ${total} files`,
          elapsed_seconds: result?.elapsed_seconds ?? r.progress?.elapsed_seconds,
        },
        completedResult: assemblyId ? null : result,
        pendingAssemblyId: assemblyId && !wasWatching ? assemblyId : null,
      }))

      // Only pull the modal onto this run if the user isn't already watching a
      // DIFFERENT run — otherwise a background completion yanks them off the
      // logs they're actually reading. The run stays listed as complete either
      // way, so its View Results button is never lost.
      setOpenTaskId(prev => (prev === null || prev === taskId ? taskId : prev))

      if (assemblyId && wasWatching) {
        setTimeout(() => {
          toast.success('Assembly analysis complete! Review the BOM structure...')
          history.push({
            pathname: `/bom-approval/${assemblyId}`,
            state: {
              folderName: run?.meta?.folderName,
              spacePath: run?.meta?.spacePath,
              fileIds: run?.meta?.fileIds || [],
            }
          })
          handlers.onComplete?.(result)
          removeRun(taskId)
        }, 1500)
      } else if (assemblyId) {
        toast.success(`Assembly analysis complete in ${run?.meta?.folderName || 'another project'} — ready for BOM approval.`)
      }
    } else if (status === 'FAILURE') {
      const folder = run?.meta?.folderName
      patchRun(taskId, r => ({
        status: 'FAILURE',
        progress: { ...r.progress, message: error || 'Analysis failed' },
      }))
      // Don't steal the modal off a run the user is actively watching.
      setOpenTaskId(prev => (prev === null || prev === taskId ? taskId : prev))
      handlers.onError?.(
        folder ? `${folder}: ${error || 'Analysis failed'}` : (error || 'Analysis failed')
      )
    }
  }, [patchRun, removeRun, history])

  const handlePollError = useCallback((taskId, message) => {
    const folder = runsRef.current[taskId]?.meta?.folderName
    patchRun(taskId, r => ({
      status: 'FAILURE',
      progress: { ...r.progress, message },
    }))
    setOpenTaskId(prev => (prev === null || prev === taskId ? taskId : prev))
    handlersRef.current[taskId]?.onError?.(folder ? `${folder}: ${message}` : message)
  }, [patchRun])

  // ---- Stop / cancel ---------------------------------------------------
  const stopRun = useCallback(async (taskId) => {
    const run = runsRef.current[taskId]
    if (!run || run.isStopping) return
    patchRun(taskId, { isStopping: true })

    const token = sessionStorage.getItem('userToken')
    const elapsed = () => parseFloat(((Date.now() - (run.startTime || Date.now())) / 1000).toFixed(1))

    appendLog(taskId, { timestamp: elapsed(), level: 'warning', message: '[STOP] Cancellation requested...' })

    try {
      await axios.post(
        `${BASE_URL}/ocr/cancel-task/${taskId}/`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      )
      patchRun(taskId, { status: 'CANCELLED' })
      appendLog(taskId, { timestamp: elapsed(), level: 'warning', message: '[STOPPED] Analysis stopped. Cached results preserved.' })
      handlersRef.current[taskId]?.onStop?.()
    } catch (err) {
      console.error('[AnalysisRun] Failed to cancel task:', err)
      appendLog(taskId, { timestamp: elapsed(), level: 'error', message: '[ERROR] Failed to stop analysis' })
      patchRun(taskId, { isStopping: false })
    }
  }, [patchRun, appendLog, BASE_URL])

  // "View Results" — hand the result back to the page that started the run.
  const completeRun = useCallback((taskId) => {
    const run = runsRef.current[taskId]
    handlersRef.current[taskId]?.onComplete?.(run?.completedResult)
    removeRun(taskId)
  }, [removeRun])

  // "Ready for BOM approval" — a background assembly run the user now clicks.
  const goToAssembly = useCallback((taskId) => {
    const run = runsRef.current[taskId]
    if (!run?.pendingAssemblyId) return
    history.push({
      pathname: `/bom-approval/${run.pendingAssemblyId}`,
      state: {
        folderName: run.meta?.folderName,
        spacePath: run.meta?.spacePath,
        fileIds: run.meta?.fileIds || [],
      }
    })
    removeRun(taskId)
  }, [history, removeRun])

  // Close (X) — dismiss a finished run.
  const closeRun = useCallback((taskId) => {
    handlersRef.current[taskId]?.onClose?.()
    removeRun(taskId)
  }, [removeRun])

  // ---- Derived views ---------------------------------------------------
  const runList = useMemo(
    () => Object.values(runs).sort((a, b) => a.startTime - b.startTime),
    [runs]
  )
  const activeRuns = useMemo(() => runList.filter(r => !isTerminalStatus(r.status)), [runList])
  const finishedRuns = useMemo(() => runList.filter(r => isTerminalStatus(r.status)), [runList])

  // File ids currently owned by a live run. A second run in the same folder is
  // allowed, but the same file must never sit in two live tasks at once, so
  // pages use this to block those files from selection.
  const busyFileIds = useMemo(() => {
    const set = new Set()
    activeRuns.forEach(r => (r.meta?.fileIds || []).forEach(id => set.add(id)))
    return set
  }, [activeRuns])

  const activeRunsInFolder = useCallback(
    (folderName) => activeRuns.filter(r => r.meta?.folderName === folderName),
    [activeRuns]
  )

  const value = {
    runs,
    runList,
    activeRuns,
    finishedRuns,
    openTaskId,
    openRun: runs[openTaskId] || null,
    hasRuns: runList.length > 0,
    busyFileIds,
    activeRunsInFolder,
    // actions
    startRun,
    stopRun,
    completeRun,
    closeRun,
    goToAssembly,
    removeRun,
    showRun,
    minimizeRun,
  }

  return (
    <AnalysisRunContext.Provider value={value}>
      {/* One poller per run — isolated lifecycles, so runs never kill each
          other. Keyed by taskId so React mounts them independently. */}
      {runList.map(run => (
        <RunPoller
          key={run.taskId}
          taskId={run.taskId}
          folderName={run.meta?.folderName}
          baseUrl={BASE_URL}
          patchRun={patchRun}
          appendLog={appendLog}
          onTerminal={handleTerminal}
          onPollError={handlePollError}
        />
      ))}
      {children}
    </AnalysisRunContext.Provider>
  )
}

// Owns the SSE stream + polling loop for exactly ONE run. Renders nothing; it
// exists so each run has its own effect lifecycle.
function RunPoller({ taskId, folderName, baseUrl, patchRun, appendLog, onTerminal, onPollError }) {
  // Log tag only — held in a ref so it never restarts the poller effect.
  const tagRef = useRef('')
  tagRef.current = `[AnalysisRun ${folderName || '?'} ${String(taskId).slice(0, 8)}]`
  // These callbacks change identity as the runs map updates; hold them in a ref
  // so the effects below depend only on taskId and never restart mid-run.
  const cbRef = useRef({ patchRun, appendLog, onTerminal, onPollError })
  useEffect(() => {
    cbRef.current = { patchRun, appendLog, onTerminal, onPollError }
  }, [patchRun, appendLog, onTerminal, onPollError])

  // ---- SSE: live activity log ------------------------------------------
  useEffect(() => {
    if (!taskId) return

    const token = sessionStorage.getItem('userToken')
    const streamUrl = `${(baseUrl || '').trim()}/ocr/status/stream/${taskId}/?token=${encodeURIComponent(token || '')}`

    let eventSource
    try {
      eventSource = new EventSource(streamUrl)
    } catch (err) {
      console.error('[AnalysisRun] Failed to construct EventSource:', err, streamUrl)
      return
    }

    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data)
        if (data.__done__) {
          eventSource.close()
          return
        }
        if (data.message) {
          cbRef.current.appendLog(taskId, {
            timestamp: typeof data.timestamp === 'number' ? data.timestamp : 0,
            level: data.level || 'info',
            message: data.message,
          })
        }
      } catch (err) {
        console.error('[AnalysisRun] Failed to parse SSE message:', err)
      }
    }

    eventSource.onerror = () => {
      // Backend closes the stream on completion too; avoid noisy reconnects.
      eventSource.close()
    }

    return () => eventSource.close()
  }, [taskId, baseUrl])

  // ---- Polling: authoritative task status ------------------------------
  useEffect(() => {
    if (!taskId) return

    let isMounted = true
    let pollCount = 0
    // Bound on elapsed wall-clock, not poll count: currentPollInterval is
    // overwritten from the server's recommended_poll_interval_ms (up to 5s), so
    // a fixed poll count spans wildly different durations. Ceiling sits above
    // Celery's 3900s hard limit so the backend always finishes the race first.
    const MAX_ELAPSED_MS = 4200 * 1000
    const pollingStartedAt = Date.now()
    // Consecutive, not cumulative: a single transient error mid-run must not
    // end a run that the server is still happily working on.
    let consecutiveErrors = 0
    const MAX_CONSECUTIVE_ERRORS = 10
    // 503 is the backend explicitly saying "try again shortly" (Redis briefly
    // unreachable) — a documented transient state, not a failure. Retried
    // without burning an error budget, but capped so a permanent 503 still ends.
    let firstTransientAt = null
    const MAX_TRANSIENT_MS = 5 * 60 * 1000
    const MAX_BACKOFF_MS = 10000
    const token = sessionStorage.getItem('userToken')
    const BASE_POLL_INTERVAL = 500
    let currentPollInterval = BASE_POLL_INTERVAL
    let timeoutId = null

    const pollStatus = async () => {
      if (!isMounted) return

      try {
        pollCount++
        const data = await getTaskStatus(taskId, token)
        if (!isMounted) return

        // A good response clears the error budget and any backoff.
        consecutiveErrors = 0
        firstTransientAt = null
        currentPollInterval = BASE_POLL_INTERVAL

        console.log(`${tagRef.current} poll #${pollCount}:`, data.status, data.progress?.message || '', data)

        const telemetry = {}
        if (data.recommended_poll_interval_ms) {
          currentPollInterval = data.recommended_poll_interval_ms
          telemetry.pollInterval = data.recommended_poll_interval_ms
        }
        if (data.concurrent_users !== undefined) telemetry.concurrentUsers = data.concurrent_users
        if (data.throttled !== undefined) telemetry.isThrottled = data.throttled

        if (data.status === 'SUCCESS') {
          cbRef.current.onTerminal(taskId, { status: 'SUCCESS', result: data.result })
          return // Stop polling
        }
        if (data.status === 'FAILURE') {
          cbRef.current.onTerminal(taskId, { status: 'FAILURE', error: data.error })
          return // Stop polling
        }

        // Activity log is driven solely by the SSE stream; the polling
        // snapshot's status_log is frozen mid-run and would clobber it.
        cbRef.current.patchRun(taskId, {
          ...telemetry,
          status: data.status,
          ...(data.status === 'PROGRESS' && data.progress ? { progress: data.progress } : {}),
        })

        if (Date.now() - pollingStartedAt >= MAX_ELAPSED_MS) {
          cbRef.current.onPollError(taskId, 'Analysis timeout - please check server logs')
          return // Stop polling
        }

        timeoutId = setTimeout(pollStatus, currentPollInterval)
      } catch (err) {
        console.error('[AnalysisRun] Error polling status:', err)

        // 503 = "task queue backend temporarily unreachable, try again shortly"
        // (see TaskStatusView). Retry without spending the error budget, but
        // give up if it never clears.
        const isTransient = err?.response?.status === 503
        if (isTransient) {
          if (firstTransientAt === null) firstTransientAt = Date.now()
          if (Date.now() - firstTransientAt < MAX_TRANSIENT_MS) {
            currentPollInterval = Math.min(currentPollInterval * 2, MAX_BACKOFF_MS)
            timeoutId = setTimeout(pollStatus, currentPollInterval)
            return
          }
        }

        consecutiveErrors++
        if (consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) {
          cbRef.current.onPollError(taskId, 'Lost connection to server')
          return // Stop polling
        }
        // Back off so retries don't pile pressure on whatever is struggling.
        currentPollInterval = Math.min(
          BASE_POLL_INTERVAL * 2 ** consecutiveErrors,
          MAX_BACKOFF_MS,
        )
        timeoutId = setTimeout(pollStatus, currentPollInterval)
      }
    }

    pollStatus()

    return () => {
      isMounted = false
      if (timeoutId) clearTimeout(timeoutId)
    }
  }, [taskId])

  return null
}
